import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, errMsg, ROLE_LABEL } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/opl/Common";
import { Field, GoldBtn, GhostBtn, ImageUpload } from "@/components/opl/Crud";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

function Permissions() {
  const [d, setD] = useState(null);
  const load = () => api.get("/roles").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <Spinner />;
  const toggle = async (role, perm, on) => {
    const perms = on ? [...role.permissions, perm] : role.permissions.filter((p) => p !== perm);
    try { await api.put(`/roles/${role.name}`, { permissions: perms }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="surface rounded-xl overflow-x-auto" data-testid="permissions-matrix">
      <table className="text-sm w-full">
        <thead><tr className="text-[11px] uppercase tracking-widest text-slate-400 border-b border-white/5"><th className="p-3 text-left">Permission</th>{d.roles.map((r) => <th key={r.name} className="p-3">{ROLE_LABEL[r.name]}</th>)}</tr></thead>
        <tbody>{d.all_permissions.map((p) => (
          <tr key={p} className="border-b border-white/[0.03]"><td className="p-3 num text-xs">{p}</td>
            {d.roles.map((r) => <td key={r.name} className="p-3 text-center"><Checkbox checked={r.permissions.includes(p)} onCheckedChange={(c) => toggle(r, p, c)} data-testid={`perm-${r.name}-${p}`} /></td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Spinner />;
  const F = (name, label, type = "text") => <Field f={{ name, label, type }} value={s[name]} onChange={(v) => setS((x) => ({ ...x, [name]: v }))} />;
  const backup = async () => {
    const r = await api.get("/admin/backup");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" }));
    a.download = `opl_backup_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
  };
  return (
    <div className="space-y-8">
      <PageHeader title="Settings & Permissions" crumbs={["Settings"]} />
      <section className="surface rounded-2xl p-6 grid sm:grid-cols-2 gap-4" data-testid="org-settings">
        <p className="eyebrow sm:col-span-2">Organizer information (shown on public site)</p>
        {F("org_name", "Organization")}{F("tournament", "Tournament name")}{F("contact_email", "Contact email")}{F("contact_phone", "Contact phone")}{F("venue", "Venue")}
        <Field f={{ name: "about", label: "Tournament introduction", type: "textarea", full: true }} value={s.about} onChange={(v) => setS((x) => ({ ...x, about: v }))} />
        <div className="sm:col-span-2"><GoldBtn testid="save-settings-btn" onClick={async () => { try { await api.put("/settings", s); toast.success("Settings saved"); } catch (e) { toast.error(errMsg(e)); } }}>Save settings</GoldBtn></div>
      </section>
      <section className="surface rounded-2xl p-6 space-y-3" data-testid="sponsor-settings">
        <p className="eyebrow">Sponsors (shown on the LED display)</p>
        {(s.sponsors || []).map((sp, i) => (
          <div key={i} className="flex flex-wrap items-center gap-3">
            <Input value={sp.name} placeholder="Sponsor name" className="w-56" data-testid={`sponsor-name-${i}`} onChange={(e) => setS((x) => ({ ...x, sponsors: x.sponsors.map((y, j) => (j === i ? { ...y, name: e.target.value } : y)) }))} />
            <ImageUpload value={sp.logo} folder="images" testid={`sponsor-logo-${i}`} onChange={(v) => setS((x) => ({ ...x, sponsors: x.sponsors.map((y, j) => (j === i ? { ...y, logo: v } : y)) }))} />
            <button className="text-xs text-red-300" data-testid={`sponsor-remove-${i}`} onClick={() => setS((x) => ({ ...x, sponsors: x.sponsors.filter((_, j) => j !== i) }))}>Remove</button>
          </div>
        ))}
        <div className="flex gap-2">
          <GhostBtn testid="add-sponsor-btn" onClick={() => setS((x) => ({ ...x, sponsors: [...(x.sponsors || []), { name: "", logo: "" }] }))}>Add sponsor</GhostBtn>
          <GoldBtn testid="save-sponsors-btn" onClick={async () => { try { await api.put("/settings", s); toast.success("Sponsors saved"); } catch (e) { toast.error(errMsg(e)); } }}>Save sponsors</GoldBtn>
        </div>
      </section>
      <section><p className="eyebrow mb-3">Role permissions</p><Permissions /></section>
      <section className="grid md:grid-cols-2 gap-5">
        <div className="surface rounded-2xl p-6">
          <p className="eyebrow">Backup</p>
          <p className="text-sm text-slate-400 mt-2">Download a full JSON snapshot of all auction records (passwords excluded). Run before and after every auction session.</p>
          <GhostBtn testid="backup-btn" className="mt-4" onClick={backup}><Download className="h-4 w-4" />Download backup</GhostBtn>
        </div>
        <div className="surface rounded-2xl p-6 space-y-3">
          <p className="eyebrow">Change my password</p>
          <Input type="password" placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password-input" />
          <Input type="password" placeholder="New password (min 8)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="new-password-input" />
          <GhostBtn testid="change-password-btn" onClick={async () => { try { await api.post("/auth/change-password", pw); toast.success("Password changed. Please log in again."); localStorage.removeItem("opl_token"); window.location.href = "/login"; } catch (e) { toast.error(errMsg(e)); } }}>Update password</GhostBtn>
        </div>
      </section>
    </div>
  );
}
